import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { type Locale, type DateRange } from "@/shared/utils";
import { usePagination, type UsePaginationReturn } from "@/shared/hooks";
import { type SortConfig } from "../types";
import { useHiringGetApplicationsQuery } from "../api";
import { STATUS_MAP } from "../consts";
import { type ApplicationDisplayStatus } from "../utils";
import { useApplicationsFilter, hasActiveFilters } from "./useApplicationsFilter";
import { useApplicationsSort, toggleSort } from "./useApplicationsSort";

export interface UseHiringListPageReturn {
  data: ReturnType<typeof useHiringGetApplicationsQuery>["data"];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  search: string;
  setSearch: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  statusFilter: ApplicationDisplayStatus | null;
  setStatusFilter: (value: ApplicationDisplayStatus | null) => void;
  statusOptions: Array<{ label: string; value: ApplicationDisplayStatus | null }>;
  activeFilters: boolean;
  view: "table" | "cards";
  setView: (value: "table" | "cards") => void;
  locale: Locale;
  sortConfig: SortConfig | null;
  handleSort: (key: SortConfig["key"]) => void;
  filteredApplications: ReturnType<typeof useApplicationsFilter>;
  sortedApplications: ReturnType<typeof useApplicationsSort>;
  pagination: UsePaginationReturn;
  pageApplications: ReturnType<typeof useApplicationsFilter>;
  handleResetFilters: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useHiringListPage(): UseHiringListPageReturn {
  const { i18n, t } = useTranslation("HiringPage");
  const locale = i18n.language as Locale;

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [statusFilter, setStatusFilter] = useState<ApplicationDisplayStatus | null>(null);

  const { data, isLoading, isFetching, isError } = useHiringGetApplicationsQuery(
    statusFilter === "hired" ? { stage: "hired" } : { status: statusFilter || undefined },
  );

  const statusOptions = useMemo(() => {
    const options: Array<{ label: string; value: ApplicationDisplayStatus | null }> = [
      { label: t("filters.allStatuses"), value: null },
    ];
    for (const [key, config] of Object.entries(STATUS_MAP)) {
      options.push({ label: t(config.label), value: key as ApplicationDisplayStatus });
    }
    return options;
  }, [t]);

  const [view, setView] = useState<"table" | "cards">("cards");

  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  function handleSort(key: SortConfig["key"]) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  const filterState = useMemo(
    () => ({
      search,
      dateRange,
      statusFilter: statusFilter || null,
    }),
    [search, dateRange, statusFilter],
  );

  const filteredApplications = useApplicationsFilter(data, filterState);
  const sortedApplications = useApplicationsSort(filteredApplications, sortConfig);

  const activeFilters = hasActiveFilters(filterState);

  const pagination = usePagination(filteredApplications.length, 10, [search, dateRange, statusFilter]);

  const pageApplications = sortedApplications.slice(pagination.startIndex, pagination.endIndex);

  useEffect(() => {
    pagination.setPage(1);
  }, [search, dateRange, statusFilter, pagination]);

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setStatusFilter(null);
    pagination.setPage(1);
  }

  return {
    data,
    isLoading,
    isFetching,
    isError,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    view,
    setView,
    locale,
    sortConfig,
    handleSort,
    filteredApplications,
    sortedApplications,
    pagination,
    pageApplications,
    handleResetFilters,
    t,
  };
}
